import type { CoreProductEntity } from '@ts/core/src/modules/coreproduct/coreproduct.entity'
import type { CategorySchemaResponse } from '@ts/core/src/modules/category/category.schema.objects'
import type { ProductSchemaOffer } from '@ts/core/src/modules/coreproduct/coreproduct.objects'
import type { ReviewEntity } from '@ts/core/src/modules/review/review.entity'
import type { StoreEntity } from '@ts/core/src/modules/store/store.entity'
import imageProxy from './imageProxy'

type JsonLdPrimitive = string | number | boolean | null
type JsonLdValue = JsonLdPrimitive | JsonLd | JsonLdValue[]
export type JsonLd = { [key: string]: JsonLdValue | undefined }

/**
 * This schema is generally based upon a schema provided to us by HAVAS (3rd Party TC uses for SEO)
 */

const SITE_ORIGIN = 'https://www.thirstycamel.com.au'
const SEARCH_PATH = '/search?search={search_term_string}'
const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
] as const

export const schemaConfig = {
  siteOrigin: SITE_ORIGIN,
  homepageUrl: `${SITE_ORIGIN}/`,
  organizationName: 'Thirsty Camel',
  priceCurrency: 'AUD',
  logoUrl: 'https://storage.googleapis.com/existing-tc-imagery/logo_keylined.png',
  sameAs: [
    'https://www.instagram.com/thirstycamelbottleshops/',
    'https://www.facebook.com/thirstycamelbottleshops',
    'https://www.linkedin.com/company/thirsty-camel-bottleshops',
  ] as string[],
  customerServiceTelephone: '03 8573 4100',
}

const schemaEntityIds = {
  organization: `${schemaConfig.homepageUrl}#organization`,
  website: `${schemaConfig.homepageUrl}#website`,
}

// These characters are escaped because the JSON is injected into a script tag w/ dangerouslySetInnerHTML
export const serialiseJsonLd = (value: JsonLd) =>
  JSON.stringify(value).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026')

const withContext = (value: JsonLd): JsonLd => ({
  '@context': 'https://schema.org',
  ...value,
})

const buildNodeId = (url: string, suffix: string) => `${url}#${suffix}`

const buildOrganizationSchema = (): JsonLd => ({
  '@type': 'Organization',
  '@id': schemaEntityIds.organization,
  name: schemaConfig.organizationName,
  url: schemaConfig.homepageUrl,
  logo: {
    '@type': 'ImageObject',
    url: schemaConfig.logoUrl,
  },
  sameAs: schemaConfig.sameAs,
  contactPoint: schemaConfig.customerServiceTelephone
    ? [
        {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          telephone: schemaConfig.customerServiceTelephone,
        },
      ]
    : undefined,
})

const buildWebsiteSchema = (): JsonLd => ({
  '@type': 'WebSite',
  '@id': schemaEntityIds.website,
  name: schemaConfig.organizationName,
  url: schemaConfig.homepageUrl,
  publisher: {
    '@id': schemaEntityIds.organization,
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${schemaConfig.siteOrigin}${SEARCH_PATH}`,
    'query-input': 'required name=search_term_string',
  },
})

const getProductName = (product: CoreProductEntity) => {
  if (product?.bundle?.title) return product.bundle.title

  return [product?.productGroup?.brand?.name, product?.productGroup?.name].filter(Boolean).join(' ')
}

const getProductDescription = (product: CoreProductEntity) =>
  product?.bundle?.description || product?.description || product?.productGroup?.description

const getProductImages = (product: CoreProductEntity) => {
  const images = Array.from(
    new Set(
      (product?.media || [])
        .map(media => imageProxy(media?.filename, { height: 1200 }))
        .filter(Boolean),
    ),
  )

  return images.length ? images : [schemaConfig.logoUrl]
}

const getAggregateRating = (product: CoreProductEntity) => {
  const ratingValue = product?.productGroup?.rating
  const reviewCount = product?.productGroup?.numberOfReviews

  if (typeof ratingValue !== 'number' || typeof reviewCount !== 'number' || reviewCount <= 0) {
    return undefined
  }

  return {
    '@type': 'AggregateRating',
    ratingValue,
    reviewCount,
  }
}

const getProductReviews = (reviews?: ReviewEntity[] | null) => {
  if (!reviews?.length) return undefined

  return reviews.map(review => ({
    '@type': 'Review',
    author: review?.firstName
      ? {
          '@type': 'Person',
          name: review.firstName,
        }
      : undefined,
    datePublished: review?.createdAt ? new Date(review.createdAt).toISOString() : undefined,
    name: review?.title,
    reviewBody: review?.message,
    reviewRating:
      typeof review?.rating === 'number'
        ? {
            '@type': 'Rating',
            ratingValue: review.rating,
            bestRating: 5,
            worstRating: 1,
          }
        : undefined,
  }))
}

const getProductOffer = (
  schemaOffer: ProductSchemaOffer | null | undefined,
  canonicalUrl: string,
) => {
  if (typeof schemaOffer?.price !== 'number' || !Number.isFinite(schemaOffer.price)) {
    return undefined
  }

  return {
    '@type': 'Offer',
    price: schemaOffer.price,
    priceCurrency: schemaOffer.priceCurrency || schemaConfig.priceCurrency,
    availability: schemaOffer.availability,
    url: canonicalUrl,
    priceValidUntil: schemaOffer.priceValidUntil,
  }
}

const formatSchemaTime = (value: string) => {
  const match = value.match(/^(\d{1,2}):(\d{2})(am|pm)$/)

  if (!match) return undefined

  const [, hoursPart, minutesPart, meridiem] = match
  let hours = Number(hoursPart)
  const minutes = Number(minutesPart)

  if (
    Number.isNaN(hours) ||
    hours < 1 ||
    hours > 12 ||
    Number.isNaN(minutes) ||
    minutes < 0 ||
    minutes > 59
  ) {
    return undefined
  }

  if (meridiem === 'am') {
    hours = hours === 12 ? 0 : hours
  } else {
    hours = hours === 12 ? 12 : hours + 12
  }

  // OpeningHoursSpecification wants 24-hour times.
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const getStoreSchemaName = (store: StoreEntity) => {
  const name = store?.name?.trim()

  if (!name) return undefined
  if (name.toLowerCase().startsWith('thirsty camel')) return name

  return `Thirsty Camel ${name}`
}

const getOpeningHoursSpecification = (store: StoreEntity) => {
  const hours = DAYS_OF_WEEK.map(({ key, label }) => {
    const hours = store?.hours?.[key]
    const opens = hours?.[0] ? formatSchemaTime(hours[0]) : undefined
    const closes = hours?.[1] ? formatSchemaTime(hours[1]) : undefined

    // Only emit a day when we have a complete, valid open/close pair.
    if (
      !hours ||
      !Array.isArray(hours) ||
      hours.length !== 2 ||
      !hours[0] ||
      !hours[1] ||
      !opens ||
      !closes
    ) {
      return undefined
    }

    return {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: label,
      opens,
      closes,
    }
  }).filter(Boolean)

  return hours.length ? hours : undefined
}

const getStoreAddressParts = (address?: string) => {
  // Store addresses are inline in our db, so split to fill all required points of the schema and keep the last part as locality.
  const parts = (address ? address.split(',') : []).map(part => part.trim()).filter(Boolean)

  if (!parts.length) {
    return {
      streetAddress: undefined,
      addressLocality: undefined,
    }
  }

  if (parts.length === 1) {
    return {
      streetAddress: parts[0],
      addressLocality: undefined,
    }
  }

  return {
    streetAddress: parts.slice(0, -1).join(', '),
    addressLocality: parts[parts.length - 1],
  }
}

export const buildHomepageSchema = () => {
  return withContext({
    '@graph': [buildOrganizationSchema(), buildWebsiteSchema()],
  })
}

export const buildProductSchema = (
  product: CoreProductEntity,
  canonicalUrl: string,
  schemaOffer?: ProductSchemaOffer | null,
  reviews?: ReviewEntity[] | null,
): JsonLd | undefined => {
  const webPageId = buildNodeId(canonicalUrl, 'webpage')
  const productId = buildNodeId(canonicalUrl, 'product')
  const sku = product?.barcodes?.[0] || product?.liquorfileNo || product?.slug
  const offers = getProductOffer(schemaOffer, canonicalUrl)
  const aggregateRating = getAggregateRating(product)
  const review = getProductReviews(reviews)

  if (!offers && !aggregateRating && !review) {
    return undefined
  }

  const productNode: JsonLd = {
    '@type': 'Product',
    '@id': productId,
    url: canonicalUrl,
    name: getProductName(product),
    image: getProductImages(product),
    description: getProductDescription(product),
    sku,
    gtin13: product?.barcodes?.[0]?.length === 13 ? product?.barcodes?.[0] : undefined,
    brand: product?.productGroup?.brand?.name
      ? {
          '@type': 'Brand',
          name: product.productGroup.brand.name,
        }
      : undefined,
    offers,
    aggregateRating,
    review,
    mainEntityOfPage: {
      '@id': webPageId,
    },
  }

  return withContext({
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': webPageId,
        url: canonicalUrl,
        isPartOf: {
          '@id': schemaEntityIds.website,
        },
        about: {
          '@id': schemaEntityIds.organization,
        },
        mainEntity: {
          '@id': productId,
        },
      },
      productNode,
    ],
  })
}

export const buildCategorySchema = (
  category: CategorySchemaResponse,
  canonicalUrl: string,
): JsonLd | undefined => {
  if (!category?.items?.length) {
    return undefined
  }

  const webPageId = buildNodeId(canonicalUrl, 'webpage')
  const collectionPageId = buildNodeId(canonicalUrl, 'collection-page')
  const itemListId = buildNodeId(canonicalUrl, 'item-list')
  const description = category.metaDescription ?? undefined

  return withContext({
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': webPageId,
        url: canonicalUrl,
        isPartOf: {
          '@id': schemaEntityIds.website,
        },
        about: {
          '@id': schemaEntityIds.organization,
        },
        mainEntity: {
          '@id': collectionPageId,
        },
      },
      {
        '@type': 'CollectionPage',
        '@id': collectionPageId,
        url: canonicalUrl,
        name: category.name,
        description,
        mainEntityOfPage: {
          '@id': webPageId,
        },
        mainEntity: {
          '@id': itemListId,
        },
      },
      {
        '@type': 'ItemList',
        '@id': itemListId,
        itemListElement: category.items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          url: item.url.startsWith('http') ? item.url : `${schemaConfig.siteOrigin}${item.url}`,
          image: item.image ? imageProxy(item.image, { height: 1200 }) : undefined,
        })),
      },
    ],
  })
}

export const buildStoreSchema = (store: StoreEntity): JsonLd => {
  const [longitude, latitude] = store?.point?.coordinates || []
  const { streetAddress, addressLocality } = getStoreAddressParts(store?.address)

  return withContext({
    '@type': 'LiquorStore',
    name: getStoreSchemaName(store),
    address: {
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality,
      addressRegion: store?.location,
      postalCode: store?.postcode,
      addressCountry: 'AU',
    },
    telephone: store?.phoneNumber,
    openingHoursSpecification: getOpeningHoursSpecification(store),
    geo:
      // Only emit geo when both coordinates are present.
      typeof latitude === 'number' && typeof longitude === 'number'
        ? {
            '@type': 'GeoCoordinates',
            latitude,
            longitude,
          }
        : undefined,
    url: `${schemaConfig.siteOrigin}/stores/${store?.slug}`,
    hasMap: store?.placeId
      ? `https://www.google.com/maps/dir/?api=1&destination_place_id=${
          store.placeId
        }&destination=${encodeURIComponent(`${store.name}, ${store.address} ${store.postcode}`)}`
      : undefined,
  })
}
